import imagesLoaded from 'imagesloaded';

export function lang(toTranslate) {
    const dico = {
        lastName: 'nom de famille',
        phone: 'numéro de téléphone',
        firstName: 'prénom',
        email: 'addresse mail'
    }
    return dico[toTranslate] || toTranslate
}

/**
 * Recursively merge own and inherited enumerable string keyed properties of source objects into the destination object
 * @param {object} destination destination object
 * @param {object[]} sources
 * 
 * @returns {object} new object that contains all the properties from both objects
 * 
 * @example
 * 
 * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
 * const object = { a: [{ b: 2 }, { d: 4 }] };
 * const other = { a: [{ c: 3 }, { e: 5 }] };
 * mergeObjects(object, other); 
 */
export function mergeObjects(destination, ...sources) {
    for (let source of sources) {
        for (let key in source) {
            if (source.hasOwnProperty(key)) {
                if (typeof source[key] === 'object' && source[key] !== null) {
                    destination[key] = destination[key] || {};
                    mergeObjects(destination[key], source[key]);
                } else {
                    destination[key] = source[key];
                }
            }
        }
    }
    return destination;
}

const breakpoints = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200
};

const UA = {
    isIOS: () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
    isMac: () => /Macintosh|Mac OS X/.test(navigator.userAgent),
    isFirefox: () => navigator.userAgent.indexof('Firefox') > -1
}

/**
 * @param {HTMLElement} el
*/
const isElementIntoView = el => {
    const position = el.getBoundingClientRect(); // checking whether fully visible

    if (position.top >= 0 && position.bottom <= window.innerHeight) {
        return true;
    } // checking for partial visibility


    if (position.top < window.innerHeight && position.bottom >= 0) {
        return true;
    }
};

var camelize = function camelize(str) {
    if (str) {
        var text = str.replace(/[-_\s.]+(.)?/g, function (_, c) {
            return c ? c.toUpperCase() : '';
        });
        return "".concat(text.substr(0, 1).toLowerCase()).concat(text.substr(1));
    }
};



/**
 * @param {HTMLElement} el
 * @param {string} data
 * @return {object}
 */
var getData = (el, data) => {
    try {
        return JSON.parse(el.dataset[camelize(data)]);
    } catch (e) {
        return el.dataset[camelize(data)];
    }
};

/**
 * Retrieves current screen breakpoint
 * @returns {{currentBreakpoint: string, breakpointStartVal: number}}
 */
const getCurrentScreenBreakpoint = () => {
    let currentBreakpoint = '';

    if (window.innerWidth >= breakpoints.xl) {
        currentBreakpoint = 'xl';
    } else if (window.innerWidth >= breakpoints.lg) {
        currentBreakpoint = 'lg';
    } else if (window.innerWidth >= breakpoints.md) {
        currentBreakpoint = 'md';
    } else {
        currentBreakpoint = 'sm';
    }

    const breakpointStartVal = breakpoints[currentBreakpoint];
    return {
        currentBreakpoint,
        breakpointStartVal
    };
};

const utils = {
    breakpoints,
    getCurrentScreenBreakpoint,
    getData,
    isElementIntoView
}

/*-----------------------------------------------
| Zanimation logic
-----------------------------------------------*/

const filterBlur = () => {
    let blur = 'blur(5px)';
    const isIpadIphoneMacFirefox = (UA.isIOS() || UA.isMac()) && UA.isFirefox();

    if (isIpadIphoneMacFirefox) {
        blur = 'blur(0px)';
    }

    return blur;
};

const zanimationEffects = {
    "default": {
        from: {
            opacity: 0,
            y: 70
        },
        to: {
            opacity: 1,
            y: 0
        },
        ease: 'CubicBezier',
        duration: 0.8,
        delay: 0
    },
    'slide-down': {
        from: {
            opacity: 0,
            y: -70
        },
        to: {
            opacity: 1,
            y: 0
        },
        ease: 'CubicBezier',
        duration: 0.8,
        delay: 0
    },
    'slide-left': {
        from: {
            opacity: 0,
            x: 70
        },
        to: {
            opacity: 1,
            x: 0
        },
        ease: 'CubicBezier',
        duration: 0.8,
        delay: 0
    },
    'slide-right': {
        from: {
            opacity: 0,
            x: -70
        },
        to: {
            opacity: 1,
            x: 0
        },
        ease: 'CubicBezier',
        duration: 0.8,
        delay: 0
    },
    'zoom-in': {
        from: {
            scale: 0.9,
            opacity: 0,
            filter: filterBlur()
        },
        to: {
            scale: 1,
            opacity: 1,
            filter: 'blur(0px)'
        },
        delay: 0,
        ease: 'CubicBezier',
        duration: 0.8
    },
    'zoom-out': {
        from: {
            scale: 1.1,
            opacity: 1,
            filter: filterBlur()
        },
        to: {
            scale: 1,
            opacity: 1,
            filter: 'blur(0px)'
        },
        delay: 0,
        ease: 'CubicBezier',
        duration: 0.8
    },
    'zoom-out-slide-right': {
        from: {
            scale: 1.1,
            opacity: 1,
            x: -70,
            filter: filterBlur()
        },
        to: {
            scale: 1,
            opacity: 1,
            x: 0,
            filter: 'blur(0px)'
        },
        delay: 0,
        ease: 'CubicBezier',
        duration: 0.8
    },
    'zoom-out-slide-left': {
        from: {
            scale: 1.1,
            opacity: 1,
            x: 70,
            filter: filterBlur()
        },
        to: {
            scale: 1,
            opacity: 1,
            x: 0,
            filter: 'blur(0px)'
        },
        delay: 0,
        ease: 'CubicBezier',
        duration: 0.8
    },
    'blur-in': {
        from: {
            opacity: 0,
            filter: filterBlur()
        },
        to: {
            opacity: 1,
            filter: 'blur(0px)'
        },
        delay: 0,
        ease: 'CubicBezier',
        duration: 0.8
    }
};

/**
 * 
 * @param {GSAP} gsap 
 * @param {HTMLElement} el 
 * @param {CallableFunction} callback 
 * 
 * @returns {void}
 */
const zanimation = (gsap ,el, callback) => {
    const Selector = {
        DATA_ZANIM_TIMELINE: '[data-zanim-timeline]',
        DATA_KEYS: '[data-zanim-xs], [data-zanim-sm], [data-zanim-md], [data-zanim-lg], [data-zanim-xl]'
    };
    const DATA_KEY = {
        DATA_ZANIM_TRIGGER: 'data-zanim-trigger'
    };

    /*-----------------------------------------------
    |   Get Controller
    -----------------------------------------------*/

    let controllerZanim;
    const currentBreakpointName = utils.getCurrentScreenBreakpoint().currentBreakpoint;
    const currentBreakpointVal = utils.getCurrentScreenBreakpoint().breakpointStartVal;

    /**
     * @param {HTMLElement} element
     * 
     * @returns {{delay: number, duration: number, from: object, to: object}}
     */
    const getController = element => {
        let options = {};
        let controller = {};

        if (element.hasAttribute("data-zanim-".concat(currentBreakpointName))) {
            controllerZanim = "zanim-".concat(currentBreakpointName);
        } else {
            /*-----------------------------------------------
            |   Set the mobile first Animation
            -----------------------------------------------*/
            let animationBreakpoints = [];
            let attributes = element.getAttributeNames();
            attributes.forEach(function (attribute) {
                if (attribute !== DATA_KEY.DATA_ZANIM_TRIGGER && attribute.startsWith('data-zanim-')) {
                    const breakPointName = attribute.split('data-zanim-')[1];

                    if (utils.breakpoints[breakPointName] < currentBreakpointVal) {
                        animationBreakpoints.push({
                            name: breakPointName,
                            size: utils.breakpoints[breakPointName]
                        });
                    }
                }
            });
            controllerZanim = undefined;

            if (animationBreakpoints.length !== 0) {
                animationBreakpoints = animationBreakpoints.sort(function (a, b) {
                    return a.size - b.size;
                });
                const activeBreakpoint = animationBreakpoints.pop();
                controllerZanim = "zanim-".concat(activeBreakpoint.name);
            }
        }

        const userOptions = utils.getData(element, controllerZanim);
        controller = mergeObjects(options, userOptions);

        if (!(controllerZanim === undefined)) {
            if (userOptions.animation) {
                options = zanimationEffects[userOptions.animation];
            } else {
                options = zanimationEffects["default"];
            }
        }

        if (controllerZanim === undefined) {
            options = {
                delay: 0,
                duration: 0,
                ease: 'Expo.easeOut',
                from: {},
                to: {}
            };
        }
        /*-----------------------------------------------
        |   populating the controller
        -----------------------------------------------*/


        controller.delay || (controller.delay = options.delay);
        controller.duration || (controller.duration = options.duration);
        controller.from || (controller.from = options.from);
        controller.to || (controller.to = options.to);

        if (controller.ease) {
            controller.to.ease = controller.ease;
        } else {
            controller.to.ease = options.ease;
        }

        return controller;
    };
    /*-----------------------------------------------
    |   End of Get Controller
    -----------------------------------------------*/

    /*-----------------------------------------------
    |   For Timeline
    -----------------------------------------------*/


    const zanimTimeline = el.hasAttribute('data-zanim-timeline');

    if (zanimTimeline) {
        const timelineOption = utils.getData(el, 'zanim-timeline');
        const timeline = gsap.timeline(timelineOption);
        const timelineElements = el.querySelectorAll(Selector.DATA_KEYS);
        timelineElements.forEach(function (timelineEl) {
            const controller = getController(timelineEl);
            timeline.fromTo(timelineEl, controller.duration, controller.from, controller.to, controller.delay).pause();
            imagesLoaded(timelineEl, callback(timeline));
        });
    } else if (!el.closest(Selector.DATA_ZANIM_TIMELINE)) {
        /*-----------------------------------------------
        |   For single elements outside timeline
        -----------------------------------------------*/
        const controller = getController(el);
        callback(gsap.fromTo(el, controller.duration, controller.from, controller.to).delay(controller.delay).pause());
    }
    callback(gsap.timeline());
};
/*-----------------------------------------------
|    Zanimation Init
-----------------------------------------------*/

/**
 * 
 * @param {GSAP} gsap 
 * 
 * returns {void}
 */
export const zanimationInit = (gsap) => {
    const Selector = {
        DATA_ZANIM_TRIGGER: '[data-zanim-trigger]',
        DATA_ZANIM_REPEAT: '[zanim-repeat]'
    };
    const DATA_KEY = {
        DATA_ZANIM_TRIGGER: 'data-zanim-trigger'
    };
    const Events = {
        SCROLL: 'scroll'
    };
    /*-----------------------------------------------
    |   Triggering zanimation when the element enters in the view
    -----------------------------------------------*/

    const triggerZanimation = () => {
        const triggerElement = document.querySelectorAll(Selector.DATA_ZANIM_TRIGGER);
        triggerElement.forEach(function (el) {
            if (utils.isElementIntoView(el) && el.hasAttribute(DATA_KEY.DATA_ZANIM_TRIGGER)) {
                zanimation(gsap ,el, function (animation) {
                    return animation.play();
                });

                if (!document.querySelector(Selector.DATA_ZANIM_REPEAT)) {
                    el.removeAttribute(DATA_KEY.DATA_ZANIM_TRIGGER);
                }
            }
        });
    };

    triggerZanimation();
    window.addEventListener(Events.SCROLL, function () {
        return triggerZanimation();
    });
};
const gsapAnimation = {
    zanimationInit,
    zanimation
};

/**
 * Runs a function when the DOM is already available
 * @param {Function} fn
 * 
 * @returns {void}
 */
export const docReady = fn => {
    // see if DOM is already available
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fn);
    } else {
        setTimeout(fn, 1);
    }
};